import axios from 'axios';
import { handleAuthError } from '../funciones/authHelper';
import { listRecursos } from '../assets/componentes/incorporaciones/api';
import { api as apiInventario } from '../assets/componentes/inventario/api';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Fase 13 · §5.4 — Credenciales e Inventario de la ficha.
 *
 * ── UNA SOLA LECTURA PARA LAS DOS PESTAÑAS ────────────────────────────────────────────────
 *
 * `queTiene` resuelve credenciales y material en la misma pasada —3,1 ms medidos—, así que
 * partirlo en dos llamadas lo ejecutaría dos veces para responder a la misma pregunta. Y la ficha
 * necesita los dos números al montar, para los contadores de las pestañas.
 *
 * ── LA VENTANA VIAJA, AUNQUE AQUÍ NO HAYA PUERTA ──────────────────────────────────────────
 *
 * Esta pestaña **no tiene puerta de segundo factor**: el `valor` de una credencial es un
 * identificador, no un secreto. Pero uno de ellos —el usuario del NAS— vive hoy tras la puerta de
 * Soporte, y el servidor no lo manda sin ventana.
 *
 * Si quien mira ya la tiene abierta, se manda y el identificador llega. No es una puerta: es no
 * volver a tapar lo que esa persona ya puede ver en la pestaña de al lado.
 */

const cab = (ventana) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...(ventana ? { 'x-ventana-2fa': ventana } : {}),
});

const sufijo = (tipo) => (tipo === 'colaboradora' ? '?tipo=colaboradora' : '');

/** Credenciales, no deducibles y material, de una vez. */
export const fetchAccesos = async (id, tipo = null, ventana = null) => {
  try {
    const r = await axios.get(`${API_URL}/api/personas/${id}/accesos${sufijo(tipo)}`,
      { headers: cab(ventana) });
    return r.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/** Registrar un acceso a mano. Exige `incorporaciones.credenciales`. */
/* `confirmarNuevo`: si ya lo tuvo y está revocado, el servidor contesta 409 `revocada_antes` y
 * sólo lo registra si se le confirma. Ver `AccesosFicha_Controller.registrar`. */
export const registrarAcceso = async (id, recursoId, tipo = null, confirmarNuevo = false) => {
  const r = await axios.post(`${API_URL}/api/personas/${id}/accesos${sufijo(tipo)}`,
    confirmarNuevo ? { recurso_id: recursoId, confirmar_nuevo: true } : { recurso_id: recursoId },
    { headers: cab(null) });
  return r.data;
};

/** Revocar una credencial REGISTRADA, la que ya tiene fila. */
export const revocarAcceso = async (id, credId, tipo = null) => {
  const r = await axios.post(`${API_URL}/api/personas/${id}/accesos/${credId}/revocar${sufijo(tipo)}`,
    {}, { headers: cab(null) });
  return r.data;
};

/**
 * Revocar una DEDUCIDA, que todavía no tiene fila: se materializa ya revocada.
 *
 * Va por recurso y no por credencial porque **no hay id que mandar**. El identificador que queda
 * registrado lo vuelve a deducir el servidor: aquí puede no estar —lo tapa la lista blanca— y,
 * aunque estuviera, mandarlo dejaría escribir cualquier cosa en el registro de la revocación.
 */
export const revocarAccesoDeducido = async (id, recursoId, tipo = null) => {
  const r = await axios.post(
    `${API_URL}/api/personas/${id}/accesos/recurso/${recursoId}/revocar${sufijo(tipo)}`,
    {}, { headers: cab(null) });
  return r.data;
};

/**
 * El catálogo de accesos registrables, para el desplegable.
 *
 * **Se reutiliza `listRecursos` del módulo**, no se escribe otra llamada: ya existe, ya lleva el
 * Bearer por su interceptor, y duplicarla añadiría un segundo sitio donde arreglar la URL el día
 * que cambie. Escribí una a mano y estaba mal —`/config/recursos` no existe—, que es la tercera
 * vez en esta fase: **una ruta escrita a mano se comprueba pulsándola, o mejor, no se escribe.**
 *
 * El filtro por tipo va en el cliente, igual que en `DetalleAlta`, para no tocar un endpoint
 * que usan otras dos pantallas.
 */
export const fetchRecursosCredencial = async () => {
  const lista = await listRecursos();
  return (lista || []).filter((x) => x.activo && x.tipo === 'credencial');
};

/**
 * Fase 13 · desasignar un activo DESDE LA FICHA.
 *
 * ── SIN ENDPOINT NUEVO, Y ES LA DECISIÓN ──────────────────────────────────────────────────
 *
 * Se llama al atajo que el módulo de Inventario ya tiene, con su cliente y su guard:
 *
 *   `POST /api/inventario/activos/:slug/:id/devolver?scope=` · `requireTypeAccess('edit')`
 *
 * **La ficha no debe tener una puerta propia para el mismo acto.** Una ruta paralela tendría
 * que repetir el permiso —que no es `inventario.activos.edit` sino acceso POR TIPO de activo,
 * en `inv_type_access`— y el día que esa regla cambie, cambiaría en un sitio y no en el otro.
 *
 * Que el permiso sea por tipo es una virtud, no un estorbo: quien puede devolver un móvil no
 * necesariamente puede devolver un portátil, y esa regla vale desde donde sea que se pulse.
 *
 * ── EL HISTÓRICO NO SE TOCA ───────────────────────────────────────────────────────────────
 *
 * `inv_asset_events` sólo añade. Devolver **no borra la asignación**: escribe un evento
 * `devolucion` encima y deja el activo disponible. Lo que la persona tuvo sigue constando.
 */
export const devolverActivo = async (slug, assetId, scope, motivo = null) => {
  const sc = scope === 'colaborador' ? 'colab' : 'interno';
  const cuerpo = { fecha: new Date().toISOString().slice(0, 10) };
  if (motivo) cuerpo.motivo = motivo;
  return apiInventario.post(`/activos/${slug}/${assetId}/devolver?scope=${sc}`, cuerpo);
};
